import React, { useState, useEffect, useCallback, useMemo, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { KG } from '../../types';
import CreateKGModal from './CreateKGModal';
import { extractErrorMessage } from '../../utils/errorHandler';
import '../shared.css';
import './KGList.css';

const RUNNING_STATUSES = new Set(['running', 'active']);

interface KGWithStats extends KG {
  totalVersions: number;
  publishedVersions: number;
  unpublishedVersions: number;
  latestVersionId?: string;
  latestCreatedAt?: string;
  latestDataVersion?: string;
  gdsEnabled?: boolean;
}

interface InstanceInfo {
  instance_id: string;
  kg_id: string;
  kg_name: string;
  version_id: string;
  instance_type: string;
  status: string;
  default_instance: boolean;
}


const KGList: React.FC = () => {
  const [kgs, setKgs] = useState<KGWithStats[]>([]);
  // key: `${kg_id}:${version_id}` → data_version string
  const [versionDataVersionMap, setVersionDataVersionMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [allInstances, setAllInstances] = useState<InstanceInfo[]>([]);
  const [instanceError, setInstanceError] = useState('');
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearch = useDeferredValue(searchTerm);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const loadKGs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.listKGs();
      if (!response.data) return;

      const kgMap = new Map<string, KG>();
      response.data.forEach((item: any) => {
        if (!kgMap.has(item.kg_id)) {
          kgMap.set(item.kg_id, {
            kg_id: item.kg_id,
            kg_name: item.kg_name || item.attributes?.kg_name || item.kg_id,
          });
        }
      });

      const uniqueKGs = Array.from(kgMap.values());
      const dvMap: Record<string, string> = {};
      const kgsWithStats = await Promise.all(
        uniqueKGs.map(async (kg) => {
          try {
            const versionsResponse: any = await apiService.listVersions(kg.kg_id);
            if (versionsResponse.data) {
              const sorted = [...versionsResponse.data].sort((a: any, b: any) =>
                Number(b.version_id) - Number(a.version_id)
              );
              const totalVersions = sorted.length;
              const publishedVersions = sorted.filter((v: any) =>
                v.published ?? v.attributes?.published ?? false
              ).length;
              const latest = sorted[0];

              const gdsEnabled = sorted.some((v: any) =>
                v.gds_enabled ?? v.attributes?.gds_enabled ?? false
              );

              // Build per-version data_version lookup
              sorted.forEach((v: any) => {
                const dv = v.data_version || v.attributes?.data_version;
                if (dv) dvMap[`${kg.kg_id}:${v.version_id}`] = dv;
              });

              return {
                ...kg,
                totalVersions,
                publishedVersions,
                unpublishedVersions: totalVersions - publishedVersions,
                latestVersionId: latest?.version_id,
                latestCreatedAt: latest?.created_at || latest?.attributes?.created_at,
                latestDataVersion: latest?.data_version || latest?.attributes?.data_version || undefined,
                gdsEnabled,
              };
            }
          } catch {
            // Per-KG failure is non-fatal; return zero stats below
          }
          return { ...kg, totalVersions: 0, publishedVersions: 0, unpublishedVersions: 0, gdsEnabled: false };
        })
      );
      setVersionDataVersionMap(dvMap);
      setKgs(kgsWithStats);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load knowledge graphs'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInstanceData = useCallback(async () => {
    try {
      setLoadingInstances(true);
      setInstanceError('');
      const response: any = await apiService.getAllInstances();
      if (!response.data) return;

      const list: InstanceInfo[] = response.data.map((item: any) => ({
        instance_id:      item.instance_id ?? '',
        kg_id:            item.kg_id ?? '',
        kg_name:          item.kg_name || item.kg_id || 'Unknown',
        version_id:       item.version_id ?? '',
        instance_type:    item.instance_type || 'instance',
        status:           item.status || 'unknown',
        default_instance: item.default_instance ?? false,
      }));
      setAllInstances(list);
    } catch (err: any) {
      setInstanceError(extractErrorMessage(err, 'Failed to load instances'));
    } finally {
      setLoadingInstances(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadKGs(), loadInstanceData()]);
    setRefreshing(false);
  }, [loadKGs, loadInstanceData]);

  const handleKGCreated = useCallback(() => {
    setShowCreateModal(false);
    loadKGs();
  }, [loadKGs]);

  useEffect(() => {
    loadKGs();
    loadInstanceData();
  }, [loadKGs, loadInstanceData]);

  const instancesByKgId = useMemo(() => {
    const map = new Map<string, InstanceInfo[]>();
    for (const inst of allInstances) {
      const list = map.get(inst.kg_id);
      if (list) list.push(inst);
      else map.set(inst.kg_id, [inst]);
    }
    return map;
  }, [allInstances]);

  if (loading) {
    return (
      <div className="kg-list-container">
        <div className="page-loader"><div className="page-spinner" /></div>
      </div>
    );
  }

  const filteredKgs = kgs.filter((kg) => {
    const s = deferredSearch.toLowerCase();
    return kg.kg_name.toLowerCase().includes(s) || kg.kg_id.toLowerCase().includes(s);
  });


  return (
    <div className="kg-list-container">

      {/* ── Header ── */}
      <div className="kg-list-header">
        <h1>Knowledge Graphs</h1>
        <div className="header-actions">
          <input
            type="text"
            className="search-input"
            placeholder="Search KGs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="tab-btn tab-btn--ghost kg-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh"
          >
            <svg
              className={`refresh-icon${refreshing ? ' refresh-icon--spinning' : ''}`}
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
            Create New KG
          </button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}

      {/* ── KG Table ── */}
      {kgs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
              <path d="M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6" />
            </svg>
          </div>
          <p className="empty-state-title">No knowledge graphs yet</p>
          <p className="empty-state-sub">Create your first KG to get started</p>
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
            Create Knowledge Graph
          </button>
        </div>
      ) : filteredKgs.length === 0 ? (
        <div className="empty-state">
          <p>No knowledge graphs match your search.</p>
        </div>
      ) : (
        <div className="kg-table-wrapper">
          <table className="kg-table">
            <thead>
              <tr>
                <th>KG Name</th>
                <th>KG ID</th>
                <th>Versions</th>
                <th>Latest</th>
                <th>Instances</th>
                <th>GDS</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredKgs.map((kg) => {
                const kgInsts = instancesByKgId.get(kg.kg_id) ?? [];
                const kgRunning = kgInsts.filter(i => ['running', 'active'].includes(i.status.toLowerCase())).length;
                const kgStopped = kgInsts.length - kgRunning;

                return (
                  <tr
                    key={kg.kg_id}
                    className="kg-table-row"
                    onClick={() => navigate(`/kg/${kg.kg_id}`)}
                  >
                    {/* KG Name */}
                    <td className="kg-name-cell">{kg.kg_name}</td>

                    {/* KG ID */}
                    <td className="kg-id-cell">{kg.kg_id}</td>

                    {/* Versions: total + pub/unpub pills */}
                    <td>
                      <div className="versions-cell">
                        <span className="vc-total">{kg.totalVersions}</span>
                        <span className="vc-pill vc-pill--pub">{kg.publishedVersions} pub</span>
                        <span className="vc-pill vc-pill--unpub">{kg.unpublishedVersions} unpub</span>
                      </div>
                    </td>

                    {/* Latest version */}
                    <td>
                      <div className="latest-cell">
                        {kg.latestVersionId ? (
                          <>
                            <span className="lv-ver">v{kg.latestVersionId}</span>
                            {kg.latestDataVersion && (
                              <span className="lv-data-ver">
                                <span className="lv-data-ver-label">DATA VER</span>
                                <span className="lv-data-ver-val">{kg.latestDataVersion}</span>
                              </span>
                            )}
                            {kg.latestCreatedAt && (
                              <span className="lv-date">
                                {new Date(kg.latestCreatedAt).toLocaleDateString()}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="lv-none">—</span>
                        )}
                      </div>
                    </td>

                    {/* Instances */}
                    <td className="instances-cell">
                      {loadingInstances ? (
                        <span className="ic-dash">—</span>
                      ) : kgInsts.length === 0 ? (
                        <span className="ic-none">None</span>
                      ) : (
                        <span className="ic-wrap">
                          {kgRunning > 0 && (
                            <span className="ic-pill ic-pill--run">
                              <span className="ic-dot" />
                              {kgRunning} running
                            </span>
                          )}
                          {kgStopped > 0 && (
                            <span className="ic-pill ic-pill--stop">{kgStopped} stopped</span>
                          )}
                        </span>
                      )}
                    </td>

                    {/* GDS */}
                    <td className="gds-cell">
                      {kg.gdsEnabled
                        ? <span className="kg-gds-chip">✓ GDS Enabled</span>
                        : <span className="ic-dash">—</span>
                      }
                    </td>

                    {/* Actions */}
                    <td className="actions-cell" onClick={e => e.stopPropagation()}>
                      <button
                        className="tab-btn tab-btn--ghost"
                        onClick={() => navigate(`/kg/${kg.kg_id}`)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Instances section ── */}
      <div className="instances-section">
        <h2 className="section-title">
          Instances
          {allInstances.filter(i => RUNNING_STATUSES.has(i.status.toLowerCase())).length > 0 && (
            <span className="kg-running-chip">
              {allInstances.filter(i => RUNNING_STATUSES.has(i.status.toLowerCase())).length} running
            </span>
          )}
        </h2>

        {instanceError && (
          <div className="tab-banner tab-banner--error">
            <span className="tab-banner-icon">⚠</span>
            <span className="tab-banner-body">{instanceError}</span>
          </div>
        )}

        {loadingInstances ? (
          <div className="tab-loader">
            <span className="tab-spinner" />
          </div>
        ) : allInstances.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4" />
              </svg>
            </div>
            <p className="empty-state-title">No instances running</p>
            <p className="empty-state-sub">Launch an instance from a KG version to see it here</p>
          </div>
        ) : (
          <div className="kg-table-wrapper">
            <table className="kg-table inst-table">
              <thead>
                <tr>
                  <th>KG Name</th>
                  <th>KG ID</th>
                  <th>Version</th>
                  <th>Data Version</th>
                  <th>Instance ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allInstances.map((inst) => {
                  const isRunning = ['running', 'active'].includes(inst.status.toLowerCase());
                  const kg = kgs.find(k => k.kg_id === inst.kg_id);
                  const kgName = kg?.kg_name ?? inst.kg_name ?? inst.kg_id;
                  const versionPath = `/kg/${inst.kg_id}/version/${inst.version_id}`;
                  return (
                    <tr
                      key={inst.instance_id}
                      className="kg-table-row"
                      onClick={() => navigate(versionPath)}
                    >
                      <td className="kg-name-cell">{kgName}</td>
                      <td className="kg-id-cell">{inst.kg_id}</td>
                      <td><span className="lv-ver">v{inst.version_id}</span></td>
                      <td>
                        {versionDataVersionMap[`${inst.kg_id}:${inst.version_id}`]
                          ? (
                            <span className="lv-data-ver">
                              <span className="lv-data-ver-label">DATA VER</span>
                              <span className="lv-data-ver-val">
                                {versionDataVersionMap[`${inst.kg_id}:${inst.version_id}`]}
                              </span>
                            </span>
                          ) : <span className="ic-dash">—</span>
                        }
                      </td>
                      <td className="kg-id-cell inst-id-cell">{inst.instance_id}</td>
                      <td>
                        <span className="inst-type-tag">{inst.instance_type || 'instance'}</span>
                      </td>
                      <td>
                        <span className={`inst-status-badge ${isRunning ? 'inst-status--running' : 'inst-status--stopped'}`}>
                          {isRunning && <span className="ic-dot" />}
                          {inst.status}
                          {inst.default_instance && <span className="inst-default-tag">default</span>}
                        </span>
                      </td>
                      <td className="actions-cell" onClick={e => e.stopPropagation()}>
                        <button
                          className="tab-btn tab-btn--ghost"
                          onClick={() => navigate(versionPath)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateKGModal onClose={() => setShowCreateModal(false)} onCreated={handleKGCreated} />
      )}
    </div>
  );
};

export default KGList;
